/* eslint-disable react/jsx-key */
// Components==============
import Button from 'components/UI/Button/Button';
import Modal from 'components/UI/Modal/Modal';
import Spark from 'components/UI/Spark/Spark';
import TokenGetter from 'components/UI/TokenGetter/TokenGetter';
import { motion } from 'framer-motion';
import { useMediaQ } from 'hooks/useMediaQ';
import { useRedux } from 'hooks/useRedux';
import { useRootModal } from 'hooks/useRootModal';
import { useToken } from 'hooks/useToken';
import { useTranslate } from 'hooks/useTranslate';
import { ReflectContext } from 'pages/Growth/Reflect/Reflect';
import { ReflectionItemContext } from 'pages/Growth/Reflect/components/ReflectionItem/ReflectionItem';
import { ProjectDetailsContext } from 'pages/Workspace/ProjectDetails/ProjectDetails';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { groupSparksBySkill } from 'utilities/groupSparksBySkill';
import { UserCon } from '../ProjectProcess/components/EndPoint/Edit';
import SparkGoals from '../SparkGoals/SparkGoals';
import Overview from './Overview/Overview';
import './SkillModal.module.scss';
import SkillView from './SkillView/SkillView';
import SparkView from './SparkView/SparkView';
// =========================

type SkillModalContextType = {
  selected?: SkillEntity;
  setSelected: React.Dispatch<React.SetStateAction<SkillEntity | undefined>>;
  selectedSparks: SparkEntity[];
  setSelectedSparks: React.Dispatch<React.SetStateAction<SparkEntity[]>>;
  setShowOverview: React.Dispatch<React.SetStateAction<boolean>>;
  setSkillModalIsOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  inReflection: boolean;
  handleSelectSkill?: (id: number) => void;
  usedSkills?: number[];
};

export const SkillModalContext = createContext({} as SkillModalContextType);

const handleSparkRemove = (
  spark: SparkEntity,
  setSelectedSparks: React.Dispatch<React.SetStateAction<SparkEntity[]>>
) => {
  if (spark.related_sparks) {
    const allIds = [spark.id, ...spark.related_sparks.map((s) => s.id)];
    setSelectedSparks((prev) => prev.filter((s) => !allIds.includes(s.id)));
  } else {
    setSelectedSparks((prev) => prev.filter((s) => s.id !== spark.id));
  }
};

const SideModalContent = () => {
  const translate = useTranslate();

  const { setSkillModalIsOpen, selectedSparks, setSelectedSparks } = useContext(
    SkillModalContext
  );

  const { closeModal } = useRootModal();

  const { theme } = useContext(ReflectContext);
  const { activity, setSaveState, saveState, save } = useContext(
    ReflectionItemContext
  );

  const handleClose = () =>
    setSkillModalIsOpen ? setSkillModalIsOpen(false) : closeModal();

  const query = useMediaQ('min', 1325);

  const token = useToken(activity);

  return (
    <div styleName="side">
      <div styleName={!query ? 'with-token' : ''}>
        {!query && <TokenGetter activity={activity} small />}
        <div>
          <h3>{theme?.name}</h3>
          <p styleName="token-name">
            {token && 'name' in token ? token.name : token?.title}
          </p>
        </div>
      </div>
      {query && <TokenGetter activity={activity} />}
      {!!selectedSparks.length && (
        <div>
          <p styleName="spark-title">Gekoppelde sparks</p>
          <div styleName="sparks">
            {selectedSparks.map((spark) => (
              <Spark
                spark={spark}
                key={spark.id}
                onClick={() => handleSparkRemove(spark, setSelectedSparks)}
              />
            ))}
          </div>
        </div>
      )}
      <div styleName="button-wrapper">
        <Button color="grey" onClick={handleClose}>
          {translate('general.terms.cancel')}
        </Button>
        <Button
          onClick={() => {
            handleClose();
            setSaveState((prev) => ({
              ...prev,
              sparks: selectedSparks.map((spark) => spark.id),
            }));
            save({
              ...saveState,
              sparks: selectedSparks.map((spark) => spark.id),
            });
          }}
        >
          {translate('general.terms.save')}
        </Button>
      </div>
    </div>
  );
};

const SideModalEndPoint = () => {
  const translate = useTranslate();

  const { setSkillModalIsOpen, selectedSparks, setSelectedSparks } = useContext(
    SkillModalContext
  );

  const { theme, setSparks } = useContext(ProjectDetailsContext);

  const skillObj = useRedux((state) => state.skill?.entities.byId);
  const groupedSparks = groupSparksBySkill(selectedSparks, skillObj);

  const { closeModal } = useRootModal();

  const handleClose = () =>
    setSkillModalIsOpen ? setSkillModalIsOpen(false) : closeModal();

  return (
    <div styleName="side">
      <div>
        <div>
          <p styleName="endpointTitle">{theme?.name}</p>
          <p styleName="token-name">Eindpunt</p>
        </div>
      </div>

      {!!selectedSparks.length && (
        <div>
          <p styleName="spark-title">Gekoppelde sparks</p>
          <motion.div styleName="sparks-end">
            {Object.keys(groupedSparks).map((skillId) => (
              <SparkGoals
                key={skillId}
                sparks={groupedSparks[skillId].sparks}
                handleDelete={(spark) =>
                  handleSparkRemove(spark, setSelectedSparks)
                }
              />
            ))}
          </motion.div>
        </div>
      )}
      <div styleName="button-wrapper">
        <Button color="grey" onClick={handleClose}>
          {translate('general.terms.cancel')}
        </Button>
        <Button
          onClick={() => {
            setSparks(selectedSparks);
            handleClose();
          }}
        >
          {translate('general.terms.add')}
        </Button>
      </div>
    </div>
  );
};

export default function SkillModal({
  setSkillModalIsOpen,
  selectedSkill,
  handleSelectSkill,
  usedSkills,
}: {
  setSkillModalIsOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  selectedSkill?: SkillEntity;
  handleSelectSkill?: (id: number) => void;
  usedSkills?: number[];
}) {
  const { saveState, reflection, activity } = useContext(ReflectionItemContext);
  const { projectId } = useParams();
  const inReflection = !!activity;
  const inProject = !!projectId;
  const { sparks: projectSparks } = useContext(ProjectDetailsContext);

  const { handleAddSparkGoal } = useContext(UserCon);

  const saveStateSparks = useRedux((state) =>
    saveState?.sparks
      ? saveState?.sparks?.map((spark) => state.spark?.entities.byId[spark])
      : []
  ) as SparkEntity[];

  // const { projectId } = useParams() as {
  //   projectId: string;
  // };

  // const sparks = project?.sparks || [];

  // const allSparks = useRedux(
  //   (state) => state.project?.entities.byId[projectId].sparks
  // );

  // // get project sparks linked to end point
  // const projectSparks = project?.sparks || allSparks;

  const sparks = inReflection
    ? saveStateSparks.length
      ? saveStateSparks
      : reflection?.sparks.length
      ? reflection.sparks
      : []
    : projectSparks;

  const [selected, setSelected] = useState<SkillEntity | undefined>(
    selectedSkill
  );

  useEffect(() => setSelected(selectedSkill), [selectedSkill]);

  const [selectedSparks, setSelectedSparks] = useState<SparkEntity[]>(sparks);
  const [showOverview, setShowOverview] = useState(false);

  useEffect(() => {
    if (selectedSparks && selectedSparks.length && !inReflection && inProject) {
      handleAddSparkGoal(selectedSparks);
    }
  }, [selectedSparks]);

  const query = useMediaQ('min', 525);

  return (
    <SkillModalContext.Provider
      value={{
        selected,
        setSelected,
        selectedSparks,
        setSelectedSparks,
        setShowOverview,
        setSkillModalIsOpen,
        inReflection,
        handleSelectSkill,
        usedSkills,
      }}
    >
      <Modal
        setModalIsOpen={setSkillModalIsOpen}
        size="medium"
        noCross
        sideModalContent={
          inReflection ? (
            <SideModalContent />
          ) : inProject ? (
            <SideModalEndPoint />
          ) : undefined
        }
        topIcon={
          selected && query
            ? {
                image: `/storage/${selected?.icon}`,
                name: selected?.name || '',
              }
            : undefined
        }
      >
        {!selected && !showOverview ? (
          <SkillView inProject={inProject} />
        ) : selected && !showOverview && (!!inReflection || !!inProject) ? (
          <SparkView />
        ) : selected && showOverview ? (
          <Overview />
        ) : (
          <Overview />
        )}
      </Modal>
    </SkillModalContext.Provider>
  );
}
